import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import { LayoutDashboard, Package, Plus, Trash2, Save, Loader2, ShoppingBag, ShoppingBasket } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { MODULE_ICONS } from "@/lib/serviceModules";
import { ImageUploader } from "@/components/ImageUploader";
import { ProviderAnalytics } from "@/components/ProviderAnalytics";
import { GroceryLibraryModal } from "@/components/GroceryLibraryModal";
import { IncomingPrescriptions } from "@/components/IncomingPrescriptions";

const navItems = (t) => [
  { to: "/provider", icon: LayoutDashboard, label: t("dashboard"), end: true, tid: "prov-nav-dashboard" },
];

const STATUS_TINT = {
  pending: "bg-amber-500/10 text-amber-700",
  confirmed: "bg-blue-500/10 text-blue-700",
  in_progress: "bg-indigo-500/10 text-indigo-700",
  completed: "bg-emerald-500/10 text-emerald-700",
  cancelled: "bg-red-500/10 text-red-700",
};

export default function ProviderDashboard() {
  const { t, lang } = useLang();
  const { formatPrice } = useCurrency();
  const [provider, setProvider] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [profileForm, setProfileForm] = useState({ name: "", description: "", city: "", phone: "", address: "", image_url: "", banner_url: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", description: "", price: "", unit: "", image_url: "" });
  const [addingItem, setAddingItem] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const pr = await api.get("/my/provider");
    if (!pr.data) { setProvider(null); return; }
    // /my/provider omits items; fetch full detail so the "Your items" list survives reloads
    const [detail, br, an] = await Promise.all([
      api.get(`/providers/${pr.data.id}`),
      api.get("/bookings/mine"),
      api.get("/my/provider/analytics").catch(() => ({ data: null })),
    ]);
    setProvider(detail.data);
    setBookings(br.data || []);
    setAnalytics(an.data);
    setProfileForm({
      name: detail.data.name || "", description: detail.data.description || "",
      city: detail.data.city || "", phone: detail.data.phone || "",
      address: detail.data.address || "", image_url: detail.data.image_url || "",
      banner_url: detail.data.banner_url || "",
    });
  };
  useEffect(() => { load(); const iv = setInterval(load, 6000); return () => clearInterval(iv); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!provider) return;
    setSavingProfile(true); setErr("");
    try {
      const { data } = await api.patch(`/providers/${provider.id}`, profileForm);
      setProvider(data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSavingProfile(false); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!provider) return;
    setAddingItem(true); setErr("");
    try {
      await api.post(`/providers/${provider.id}/items`, {
        provider_id: provider.id,
        name: newItem.name,
        description: newItem.description,
        price: parseFloat(newItem.price) || 0,
        unit: newItem.unit || null,
        image_url: newItem.image_url || null,
      });
      setNewItem({ name: "", description: "", price: "", unit: "", image_url: "" });
      setShowAdd(false);
      const detail = await api.get(`/providers/${provider.id}`);
      setProvider(prev => ({ ...prev, ...detail.data }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Add failed");
    } finally { setAddingItem(false); }
  };

  const deleteItem = async (itemId) => {
    if (!provider) return;
    await api.delete(`/providers/${provider.id}/items/${itemId}`);
    const detail = await api.get(`/providers/${provider.id}`);
    setProvider(prev => ({ ...prev, ...detail.data }));
  };

  const updateBookingStatus = async (bid, status) => {
    await api.post(`/bookings/${bid}/status`, { status });
    const br = await api.get("/bookings/mine");
    setBookings(br.data || []);
  };

  if (!provider) {
    return (
      <DashboardLayout items={navItems(t)} title={t("dashboard")}>
        <div className="flex items-center justify-center py-24 text-[var(--so-text-soft)]"><Loader2 className="w-6 h-6 animate-spin" /></div>
      </DashboardLayout>
    );
  }

  const Icon = MODULE_ICONS[provider.module_key] || Package;
  const items = provider.items || [];
  const activeBookings = bookings.filter(b => b.status !== "completed" && b.status !== "cancelled");

  return (
    <DashboardLayout items={navItems(t)} title={lang === "ar" ? "لوحة المزود" : "Provider dashboard"}>
      <div className="max-w-4xl space-y-6">
        {/* Provider header */}
        <div className="so-card p-5 flex items-center gap-4" data-testid="provider-header">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${provider.accent}1A`, color: provider.accent }}>
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider" style={{ color: provider.accent }}>{provider.module_key.replace("_", " ")}</div>
            <div className="text-xl font-bold truncate">{provider.name}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold" data-testid="active-bookings-count">{activeBookings.length}</div>
            <div className="text-xs text-[var(--so-text-soft)]">{lang === "ar" ? "حجوزات نشطة" : "Active bookings"}</div>
          </div>
        </div>

        {err && <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="provider-error">{err}</div>}

        {/* Analytics */}
        <ProviderAnalytics analytics={analytics} formatPrice={formatPrice} accent={provider.accent} lang={lang} />

        {/* Profile form */}
        <form onSubmit={saveProfile} className="so-card p-5" data-testid="profile-form">
          <div className="flex items-center justify-between mb-4">
            <div className="font-bold text-lg">{lang === "ar" ? "معلومات المزود" : "Profile info"}</div>
            {savedFlash && <span className="text-xs text-emerald-600 font-semibold" data-testid="profile-saved-flash">{lang === "ar" ? "تم الحفظ ✓" : "Saved ✓"}</span>}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="so-input" placeholder={lang === "ar" ? "اسم المزود" : "Provider name"} value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} required data-testid="profile-name" />
            <input className="so-input" placeholder={lang === "ar" ? "المدينة" : "City"} value={profileForm.city} onChange={e => setProfileForm({ ...profileForm, city: e.target.value })} data-testid="profile-city" />
            <input className="so-input sm:col-span-2" placeholder={lang === "ar" ? "الوصف" : "Description"} value={profileForm.description} onChange={e => setProfileForm({ ...profileForm, description: e.target.value })} data-testid="profile-description" />
            <input className="so-input" placeholder={lang === "ar" ? "الهاتف" : "Phone"} value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} data-testid="profile-phone" />
            <input className="so-input" placeholder={lang === "ar" ? "العنوان" : "Address"} value={profileForm.address} onChange={e => setProfileForm({ ...profileForm, address: e.target.value })} data-testid="profile-address" />
            <ImageUploader
              value={profileForm.image_url}
              onChange={(url) => setProfileForm({ ...profileForm, image_url: url })}
              label={lang === "ar" ? "شعار / صورة" : "Logo / photo"}
              testId="profile-image-uploader"
              variant="square"
              accent={provider.accent}
              lang={lang}
            />
            <ImageUploader
              value={profileForm.banner_url}
              onChange={(url) => setProfileForm({ ...profileForm, banner_url: url })}
              label={lang === "ar" ? "صورة الغلاف" : "Banner image"}
              testId="profile-banner-uploader"
              variant="banner"
              accent={provider.accent}
              lang={lang}
            />
          </div>
          <button type="submit" disabled={savingProfile} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold" style={{ background: provider.accent }} data-testid="profile-save">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {lang === "ar" ? "حفظ" : "Save profile"}
          </button>
        </form>

        {/* Pharmacy providers get the prescription inbox instead of a catalog */}
        {provider.module_key === "pharmacy" ? (
          <IncomingPrescriptions accent={provider.accent} lang={lang} />
        ) : (
        <>
        {/* Items */}
        <div className="so-card p-5" data-testid="items-section">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="font-bold text-lg">{lang === "ar" ? "قائمة الخدمات / المنتجات" : "Your items"}</div>
            <div className="flex items-center gap-2">
              {provider.module_key === "grocery" && (
                <button
                  onClick={() => setShowLibrary(true)}
                  className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg border-2 hover:brightness-95"
                  style={{ borderColor: provider.accent, color: provider.accent }}
                  data-testid="open-grocery-library"
                >
                  <ShoppingBasket className="w-3.5 h-3.5" /> {lang === "ar" ? "استعرض المكتبة" : "Browse Library"}
                </button>
              )}
              <button onClick={() => setShowAdd(v => !v)} className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: provider.accent }} data-testid="toggle-add-item">
                <Plus className="w-3.5 h-3.5" /> {lang === "ar" ? "إضافة" : "Add item"}
              </button>
            </div>
          </div>

          {provider.module_key === "grocery" && items.length === 0 && (
            <div className="mb-3 p-3 rounded-lg border border-dashed" style={{ borderColor: `${provider.accent}66`, background: `${provider.accent}0A` }}>
              <div className="text-sm font-semibold" style={{ color: provider.accent }}>
                {lang === "ar" ? "بداية سريعة" : "Quick start"}
              </div>
              <div className="text-xs text-[var(--so-text-soft)] mt-1">
                {lang === "ar"
                  ? "استعرض مكتبتنا التي تحتوي على 58 منتجًا سودانيًا شائعًا وحدد ما هو متوفر في متجرك بنقرة واحدة."
                  : "Browse our library of 58 common Sudanese groceries and one-click add what you stock."}
              </div>
            </div>
          )}

          {showAdd && (
            <form onSubmit={addItem} className="p-3 rounded-lg border border-[var(--so-border)] mb-3 grid sm:grid-cols-2 gap-2" data-testid="add-item-form">
              <input className="so-input" placeholder={lang === "ar" ? "الاسم" : "Name"} value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required data-testid="item-name" />
              <input className="so-input" type="number" step="0.01" min="0" placeholder={lang === "ar" ? "السعر" : "Price"} value={newItem.price} onChange={e => setNewItem({ ...newItem, price: e.target.value })} required data-testid="item-price" />
              <input className="so-input sm:col-span-2" placeholder={lang === "ar" ? "الوصف" : "Description"} value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} required data-testid="item-description" />
              <input className="so-input" placeholder={lang === "ar" ? "الوحدة (اختياري)" : "Unit (e.g. per visit)"} value={newItem.unit} onChange={e => setNewItem({ ...newItem, unit: e.target.value })} data-testid="item-unit" />
              <div className="sm:col-span-2">
                <ImageUploader
                  value={newItem.image_url}
                  onChange={(url) => setNewItem({ ...newItem, image_url: url })}
                  label={lang === "ar" ? "صورة العنصر" : "Item photo"}
                  testId="item-image-uploader"
                  variant="square"
                  accent={provider.accent}
                  lang={lang}
                  maxWidth="220px"
                />
              </div>
              <button type="submit" disabled={addingItem} className="sm:col-span-2 py-2 rounded-lg font-semibold text-white disabled:opacity-50" style={{ background: provider.accent }} data-testid="item-save">
                {addingItem ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (lang === "ar" ? "حفظ العنصر" : "Save item")}
              </button>
            </form>
          )}

          {items.length === 0 ? (
            <div className="text-center py-6 text-[var(--so-text-soft)] text-sm" data-testid="items-empty">
              {lang === "ar" ? "لا توجد عناصر بعد. أضف أول عنصر لك." : "No items yet. Add your first item to start receiving bookings."}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(it => (
                <div key={it.id} className="p-3 rounded-lg border border-[var(--so-border)] flex items-center gap-3" data-testid={`prov-item-${it.id}`}>
                  {it.image_url && <img src={it.image_url} alt={it.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{it.name}</div>
                    <div className="text-xs text-[var(--so-text-soft)] line-clamp-1">{it.description}</div>
                  </div>
                  <div className="text-sm font-bold" style={{ color: provider.accent }}>{formatPrice(it.price)}{it.unit && <span className="text-[10px] text-[var(--so-text-soft)] font-normal"> / {it.unit}</span>}</div>
                  <button onClick={() => deleteItem(it.id)} className="w-8 h-8 rounded-lg text-red-500 hover:bg-red-500/10 flex items-center justify-center" data-testid={`delete-item-${it.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}

        {/* Bookings */}
        <div className="so-card p-5" data-testid="bookings-section">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag className="w-5 h-5" style={{ color: provider.accent }} />
            <div className="font-bold text-lg">{lang === "ar" ? "الحجوزات الواردة" : "Incoming bookings"}</div>
          </div>
          {bookings.length === 0 ? (
            <div className="text-center py-6 text-[var(--so-text-soft)] text-sm" data-testid="bookings-empty">
              {lang === "ar" ? "لا توجد حجوزات بعد." : "No bookings yet."}
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.map(b => (
                <div key={b.id} className="p-3 rounded-lg border border-[var(--so-border)]" data-testid={`prov-booking-${b.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{b.customer_name}</div>
                      <div className="text-xs text-[var(--so-text-soft)]">{new Date(b.created_at).toLocaleString()}</div>
                      <div className="mt-1 text-xs">
                        {b.items.length} item(s) · <span className="font-bold" style={{ color: provider.accent }}>{formatPrice(b.total)}</span>
                        {b.scheduled_at && <span className="ms-2 text-[var(--so-text-soft)]">· {new Date(b.scheduled_at).toLocaleString()}</span>}
                      </div>
                      {b.address && <div className="text-xs text-[var(--so-text-soft)] mt-1 truncate">📍 {b.address}</div>}
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TINT[b.status] || ""}`}>{b.status.replace("_", " ")}</span>
                  </div>
                  {b.status !== "completed" && b.status !== "cancelled" && (
                    <div className="mt-2 flex gap-1.5 flex-wrap">
                      {b.status === "pending" && <button onClick={() => updateBookingStatus(b.id, "confirmed")} className="text-xs px-2 py-1 rounded-md bg-blue-500 text-white" data-testid={`accept-${b.id}`}>{lang === "ar" ? "قبول" : "Confirm"}</button>}
                      {b.status === "confirmed" && <button onClick={() => updateBookingStatus(b.id, "in_progress")} className="text-xs px-2 py-1 rounded-md bg-indigo-500 text-white" data-testid={`start-${b.id}`}>{lang === "ar" ? "بدء" : "Start"}</button>}
                      {b.status === "in_progress" && <button onClick={() => updateBookingStatus(b.id, "completed")} className="text-xs px-2 py-1 rounded-md bg-emerald-600 text-white" data-testid={`complete-${b.id}`}>{lang === "ar" ? "إنهاء" : "Complete"}</button>}
                      <button onClick={() => updateBookingStatus(b.id, "cancelled")} className="text-xs px-2 py-1 rounded-md border border-[var(--so-border)]" data-testid={`cancel-${b.id}`}>{lang === "ar" ? "إلغاء" : "Cancel"}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Grocery Library Modal (only for grocery providers) */}
      {provider && provider.module_key === "grocery" && (
        <GroceryLibraryModal
          open={showLibrary}
          onClose={() => setShowLibrary(false)}
          providerId={provider.id}
          providerAccent={provider.accent}
          lang={lang}
          onAdded={async () => {
            const detail = await api.get(`/providers/${provider.id}`);
            setProvider((prev) => ({ ...prev, ...detail.data }));
          }}
        />
      )}
    </DashboardLayout>
  );
}
